import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { useUserAuth } from '../context/UserAuthContext';
import { toast } from 'sonner';
import { Loader2, LogIn, ArrowLeft, Bot } from 'lucide-react';

const LOGO_URL = "/liquicent-logo.png";

const UserLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const from = location.state?.from?.pathname || '/user/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate(from, { replace: true });
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] pt-32 pb-20 px-6">
      <Helmet>
        <title>Sign In | Liquicent</title>
      </Helmet>
      
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <img 
              src={LOGO_URL} 
              alt="Liquicent" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-2xl font-semibold text-[#0F172A]">Liquicent</span>
          </Link>
          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Welcome back</h1>
          <p className="text-[#64748B]">Sign in to manage your agent deployments</p>
        </div>

        <Card 
          className="p-8 bg-white border border-[#E5E7EB] rounded-2xl"
          style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#0F172A] text-sm font-medium">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F] focus:ring-[#1A3A8F]/20"
                placeholder="john@company.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#0F172A] text-sm font-medium">
                  Password
                </Label>
                <Link
                  to="/user/forgot-password"
                  className="text-xs text-[#1A3A8F] hover:text-[#142C6D] font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="bg-[#F6F7FB] border-[#E5E7EB] text-[#0F172A] focus:border-[#1A3A8F] focus:ring-[#1A3A8F]/20"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1A3A8F] hover:bg-[#142C6D] text-white py-6 text-base font-medium rounded-lg transition-all"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center">
            <p className="text-[#64748B] text-sm">
              Don't have an account?{' '}
              <Link to="/user/signup" className="text-[#1A3A8F] hover:text-[#142C6D] font-semibold">
                Create one
              </Link>
            </p>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#64748B] hover:text-[#1A3A8F] text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;
